import { useEffect, useState } from "react";
import { Check, CheckCheck, Pencil } from "lucide-react";
import { FormWrapper } from "@/components/molecules/FormWrapper/FormWrapper";
import { FormSelect } from "@/components/molecules/FormSelect/FormSelect";
import { OrderFormProps } from "./OrdersForm.types";
import { OrderFormData, schema, variantMap } from "../infrastructure/OrdersConstants";
import { mapToFormValues } from "../infrastructure/OrderMapper";
import { InstrumentDetail } from "./InstrumentDetail";
import { FormInput } from "@/components/molecules/FormInput/FormInput";
import { FormAutocomplete } from "@/components/molecules/FormAutocomplete/FormAutocomplete";
import { Badge } from "@/components/atoms/Badge";
import { OrderStatusEnum, InstrumentValidationEnum, Rate, OrderTypeEnum, Portfolio } from "../domain/entities/OrdersEntity";
import { Button } from "@/components/atoms/Button";
import { useConfirmDialogs } from "@/core/context/DialogContext";
import { FormSwitch } from "@/components/molecules/FormSwitch";
import { PortfolioDetail } from "./PortfolioDetail";

export const OrdersForm = ({
    onSubmit,
    onDelete,
    onContinue,
    orderSelected,
    portfolioOptions,
    instrumentOptions,
    instrumentSelected,
    rates,
    portfolios,
    setInstrumentSelected,
    formStatus
}: OrderFormProps) => {
    const [isOn, setIsOn] = useState(orderSelected ? orderSelected.orderType === OrderTypeEnum.COMPRA : true);
    const [ratesByInstrument, setRatesByInstrument] = useState<Rate[] | undefined>([]);
    const [portfolioSelected, setPortfolioSelected] = useState<Portfolio | undefined>(orderSelected?.portfolio);
    const variant = variantMap[orderSelected?.status as unknown as OrderStatusEnum] ?? "info";
    const showDialog = useConfirmDialogs();

    useEffect(() => {
        setRatesByInstrument(() => rates.filter(r => r.instrumentId === instrumentSelected?.id))
    }, [instrumentSelected]);

    const confirmSubmit = async (formData: OrderFormData) => {
        if (!formData.orderType) {
            onSubmit(formData)
        } else {
            let confirm = false;

            switch (instrumentSelected?.validation) {
                case InstrumentValidationEnum.V1:
                    confirm = await showDialog({
                        title: "Validación Exitosa de Límites",
                        description: "La transacción cumple con la validación de límites y políticas.",
                        variant: "success",
                    });
                    break;
                case InstrumentValidationEnum.V2:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por Calificación, el instrumento es inferior a AA+ y excede el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;
                case InstrumentValidationEnum.V3:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por plazo, el instrumento es superior a 1,800 días y excede el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;
                case InstrumentValidationEnum.V4:
                    confirm = await showDialog({
                        title: "Validación de Límites No Exitosa",
                        description: (
                            <div className="flex-col space-y-2">
                                <p>
                                    La transacción no cumple con el límite de concentración por emisor, la posición en ese emisor excedería el % permitido.
                                </p>
                                <p>
                                    ¿Desea gestionar una excepción con el departamento de riesgos?
                                </p>
                            </div>
                        ),
                        variant: "warning",
                    });
                    break;

                default:
                    break;
            }

            if (confirm) {
                onSubmit(formData)
            }

        }
    }

    const confirmDelete = async (id: string) => {
        const confirm = await showDialog({
            title: "¿Estás seguro?",
            description: "Esta acción eliminará el registro. ¿Deseas continuar?",
            variant: "delete",
        });

        if (confirm) {
            onDelete(id);
        }
    }

    return (
        <FormWrapper<OrderFormData>
            defaultValues={mapToFormValues(orderSelected)}
            onSubmit={confirmSubmit}
            schema={schema}
            formStatus={formStatus}
            onDelete={confirmDelete}
            canSave={orderSelected?.status !== OrderStatusEnum.FIN}
        >
            {({ setValue, getValues }) => {
                const handleRateBlur = () => {
                    const inputRate = parseFloat(getValues("rate")?.toString() ?? "0");
                    const matched = ratesByInstrument?.find(r => r.rate === inputRate);

                    if (matched) {
                        setValue("value", getValues("quantity") * (matched.price / 100));
                    } else {
                        setValue("value", 0);
                    }
                };

                return (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormSwitch
                                label={isOn ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA}
                                variant="darkInfo"
                                inactiveVariant="darkError"
                                onCheckedChange={setIsOn}
                                name="orderType" />

                            {orderSelected && (
                                <div className="flex flex-col-reverse md:flex-row md:flex-wrap justify-end gap-2 items-center">
                                    {orderSelected?.confirmNumber && (
                                        <>
                                            <Badge className="w-full md:w-auto gap-2 px-4 justify-between" variant="secondary">
                                                {`N° Operación: ${orderSelected?.confirmNumber}`}
                                                {orderSelected?.status === OrderStatusEnum.COMPLEMENTACION && (
                                                    <Button
                                                        size="xs"
                                                        variant="secondary"
                                                        className="p-1 min-w-0 "
                                                        onClick={() =>
                                                            onContinue(
                                                                mapToFormValues(orderSelected) as OrderFormData,
                                                                orderSelected?.status
                                                            )}>
                                                        <Pencil className="h-4 w-4" aria-hidden="true" />
                                                    </Button>
                                                )}
                                            </Badge>
                                        </>
                                    )}

                                    <div className="flex gap-2 w-full md:w-auto">
                                        <Badge className="w-full" variant={variant}>
                                            {orderSelected?.status}
                                        </Badge>

                                        {orderSelected?.status !== OrderStatusEnum.FIN && (
                                            <Button
                                                size="xs"
                                                onClick={() =>
                                                    onContinue(
                                                        mapToFormValues(orderSelected) as OrderFormData,
                                                        orderSelected?.status === OrderStatusEnum.REGISTRO
                                                            ? OrderStatusEnum.COMPLEMENTACION
                                                            : OrderStatusEnum.FIN
                                                    )
                                                }
                                            >
                                                {orderSelected?.status === OrderStatusEnum.REGISTRO ? (
                                                    <Check className="h-6 w-6" aria-hidden="true" />
                                                ) : (
                                                    <CheckCheck className="h-6 w-6" aria-hidden="true" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div >

                        <div className="grid grid-cols-1 gap-2">
                            <FormSelect
                                onChange={(value: string) => {
                                    const portfolio = portfolios.find(pf => pf.id === value);
                                    if (portfolio) {
                                        setPortfolioSelected(portfolio);
                                    }
                                }}
                                name="portfolio"
                                label="Portafolio"
                                options={portfolioOptions}
                            />
                            {!!portfolioSelected && (
                                <PortfolioDetail portfolio={portfolioSelected} />
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                            <FormAutocomplete
                                name="instrument"
                                label="Instrumento"
                                options={instrumentOptions}
                                onChange={setInstrumentSelected}
                                renderOption={(option) => (
                                    <span className="flex flex-col text-left h-full">
                                        <small className="font-bold">{option.label}</small>
                                        <small>{option.detail}</small>
                                    </span>
                                )}
                            />
                            {instrumentSelected && (
                                <InstrumentDetail instrument={instrumentSelected} />
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormInput name="quantity" label="Cantidad" type="number" />
                            <FormInput
                                name="rate"
                                label="Tasa de negociación"
                                type="percent"
                                onBlur={handleRateBlur}
                            />
                            <FormInput name="value" label="Valor giro" type="number" prefix="$" />
                        </div>
                    </>
                );
            }}
        </FormWrapper >
    );
};